import { Row } from "@amzn/stencil-react-components/layout";
import { SkeletonButton } from "@amzn/stencil-react-components/skeleton";
import { Text } from "@amzn/stencil-react-components/text";
import { IconArrowRight, IconRefresh, IconSize } from "@amzn/stencil-react-icons";
import Button from "@amzn/uxm-stencil-react-components/lib/src/stencil/button";
import Chip, { ChipBackgroundColor, ChipIconPosition } from "@amzn/uxm-stencil-react-components/lib/src/stencil/chip";
import { getString } from "common/uistringlabels/uiStringUtils";
import { sortingOptions } from "lib/models/aiforme-models";
import { JobInfo } from "lib/models/oui-models";
import { UserInformation } from "lib/services/user-information-service";
import React, { useEffect, useState } from "react";

import { AiForMeSearchListState } from "./useAiForMeSearchList";
import useAiForMeUseCases from "./useAiForMeUseCases";

export interface AiForMeUseCasesProps {
  updateQuery: (query: string) => void;
  batchUpdateInputs: (updates: Partial<AiForMeSearchListState>) => void;
}

export const AiForMeUseCases = (props: AiForMeUseCasesProps) => {
  const { updateQuery, batchUpdateInputs } = props;
  const { isLoadingUseCases, useCases, getUseCases, scrambleUseCases } = useAiForMeUseCases();
  const [jobInfo, setJobInfo] = useState<JobInfo | undefined>();

  useEffect(() => {
    UserInformation.current
      .getUserInformation()
      .then((userInfo) => {
        setJobInfo(userInfo.jobInfo);
        getUseCases(userInfo.jobInfo.jobFamilyName);
      })
      .catch((error) => {
        console.error("Error fetching user information:", error);
      });
  }, []);

  return (
    <>
      <Text style={{ paddingBottom: 10 }}>
        {getString("aiForMe.roleRecommendedUseCases", { role: jobInfo?.businessTitle })}
      </Text>
      {isLoadingUseCases && (
        <>
          <Row columnGap={20} padding={[0, 0, 18, 0]} alignItems="flex-end">
            <SkeletonButton width="20%" />
            <SkeletonButton width="20%" />
            <SkeletonButton width="20%" />
            <SkeletonButton width="15%" />
          </Row>
        </>
      )}
      {!isLoadingUseCases && useCases && (
        <>
          <Row columnGap={20} padding={[0, 0, 18, 0]} alignItems="flex-end">
            {useCases.slice(0, 3).map((useCase, index) => (
              <Chip
                key={index}
                touchpoint="use-case-click"
                data-testid={`use-case-${index}`}
                customData={{
                  name: useCase.name,
                  amazonianInfo: {
                    businessTitle: jobInfo?.businessTitle,
                    jobFamilyName: jobInfo?.jobFamilyName,
                  },
                }}
                onClick={() => {
                  updateQuery(useCase.name);
                  // Batch ALL updates including search text to prevent multiple API calls
                  batchUpdateInputs({
                    searchText: useCase.name,
                    selectedJobFamilies: [],
                    sortBy: sortingOptions[0],
                    isUseCaseSearch: true,
                  });
                }}
                backgroundColor={ChipBackgroundColor.Default}
                iconPosition={ChipIconPosition.Trailing}
                icon={<IconArrowRight aria-hidden={true}></IconArrowRight>}
              >
                {useCase.name}
              </Chip>
            ))}
            <Button
              key="shuffle-use-cases"
              icon={<img src="/img/lucide_shuffle.svg" alt="Shuffle icon" />}
              touchpoint="shuffle-use-case-click"
              data-testid="shuffle-use-cases"
              onClick={() => {
                scrambleUseCases();
              }}
            >
              Shuffle
            </Button>
          </Row>
        </>
      )}
    </>
  );
};
